/* eslint-disable @typescript-eslint/no-explicit-any */

import { Box, Spinner, Stack, Text } from '@chakra-ui/react';
import { UploadSimple, File, X, CheckCircle } from '@phosphor-icons/react';
import { useState } from 'react';

import { fetchAPI } from '@/api/fetch';
import { apiAdminTestEmbedding, AdminTestEmbeddingResponse } from '@/api/services';
import { useAdminAnalysis } from '@/store/adminAnalysis';
import { MAX_RESUME_FILE_SIZE, MAX_RESUME_FILE_SIZE_MB } from '@/config/constants';

export default function EmbeddingVectorSection() {
  const fileSizeLimit = MAX_RESUME_FILE_SIZE;
  const [fileUploading, setFileUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [embeddingResult, setEmbeddingResult] = useState<AdminTestEmbeddingResponse | null>(null);
  const { resumeFile, setResumeFile, clearResumeFile } = useAdminAnalysis();

  const getFileSize = (size?: number): string => {
    if (!size) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return `${(size / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const handleFileUpload = async (file: File) => {
    setResumeFile(file);
    setFileUploading(true);
    setEmbeddingResult(null);
    setFileError(null);

    if (file.size > fileSizeLimit) {
      setFileError(`File size exceeds ${MAX_RESUME_FILE_SIZE_MB} MB`);
      setFileUploading(false);
      return;
    }

    try {
      const response = await fetchAPI<AdminTestEmbeddingResponse>({
        request: () => apiAdminTestEmbedding({ payload: { file } })
      });

      if (response.success) {
        setEmbeddingResult(response.data);
      } else {
        handleFileError('Unable to test embedding', response.message);
      }
    } catch (error) {
      handleFileError('Unable to test embedding', error);
    } finally {
      setFileUploading(false);
    }
  };

  const handleFileError = (message: string, error: any) => {
    console.error(message, error);
    setFileError(message);
  };

  const onUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const resetFileState = () => {
    clearResumeFile();
    setEmbeddingResult(null);
    setFileError(null);
  };

  return (
    <Stack gap="24px" width="100%">
      <Stack gap="16px" align="flex-start" width="100%">
        {resumeFile || fileUploading ? (
          <Box display="flex" alignItems="center" width="100%">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              width="150px"
              height="150px"
              borderRadius="12px"
              border="1px solid"
              borderColor={fileError ? 'error.500' : 'primary.100'}
              bg="primary.50"
            >
              {fileUploading ? (
                <>
                  <Spinner size="lg" borderWidth="4px" color="primary.900" />
                  <Box mt="8px" fontSize="16px" color="neutrals.900" textAlign="center">
                    Uploading
                  </Box>
                </>
              ) : (
                <File size={64} color="#4e76ff" />
              )}
            </Box>
            <Box ml="32px" color="neutrals.900">
              <Box display="flex" alignItems="center" mb="4px">
                {!fileUploading && (
                  <Box mr="12px">
                    <CheckCircle size={24} color="#4e76ff" weight="fill" />
                  </Box>
                )}
                <Box fontSize="18px" color="neutrals.800" mr="16px">
                  {resumeFile?.name}
                </Box>
                {!fileUploading && (
                  <X size={16} color="#545454" onClick={resetFileState} cursor="pointer" />
                )}
              </Box>
              <Box fontSize="16px" color={fileError ? 'error.700' : 'neutrals.500'}>
                {getFileSize(resumeFile?.size)}
              </Box>
              {fileError && (
                <Box fontSize="16px" color="error.700" mt="4px">
                  {fileError}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Box
            w="100%"
            h="200px"
            border="1px dashed"
            borderColor="neutrals.300"
            borderRadius="12px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            _hover={{ borderColor: 'neutrals.300' }}
            bg="neutrals.50"
          >
            <Box
              position="relative"
              width="100%"
              height="200px"
              border="2px dashed"
              borderColor="neutrals.300"
              borderRadius="12px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              _hover={{ borderColor: 'neutrals.300' }}
              bg="neutrals.50"
            >
              <input
                id="fileInput"
                type="file"
                accept=".pdf,.txt"
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer'
                }}
                onChange={onUploadFile}
              />
              <Box display="flex" flexDirection="column" alignItems="center">
                <UploadSimple size={60} color="#404040" />
                <Box textAlign="center" color="neutrals.900">
                  Drop file here or{' '}
                  <Box as="span" color="primary.900">
                    click to upload
                  </Box>
                </Box>
                <Box color="neutrals.500" fontSize="14px">
                  Upload PDF, or TXT file types ({MAX_RESUME_FILE_SIZE_MB} MB max)
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Stack>
      <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
        Embedding Result
      </Text>
      {embeddingResult ? (
        <Stack gap="8px">
          <Text fontSize="14px" color="neutrals.600">
            <Box as="span" fontWeight="semibold">
              Provider:
            </Box>{' '}
            {embeddingResult.provider}
          </Text>
          <Text fontSize="14px" color="neutrals.600">
            <Box as="span" fontWeight="semibold">
              Vector dimensions:
            </Box>{' '}
            {embeddingResult.vector.length}
          </Text>
          <Box
            bg="neutrals.50"
            border="1px solid"
            borderColor="neutrals.200"
            borderRadius="8px"
            p="12px"
            maxH="200px"
            overflowY="auto"
          >
            <Text fontSize="12px" color="neutrals.700" fontFamily="mono">
              [{embeddingResult.vector.slice(0, 20).join(', ')}
              {embeddingResult.vector.length > 20 ? ', ...' : ''}]
            </Text>
          </Box>
        </Stack>
      ) : (
        <Text>No embedding result yet.</Text>
      )}
    </Stack>
  );
}
